import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { userService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Input,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserX,
  UserCog,
  Users,
  Eye,
  Phone,
  Mail,
  Calendar,
  Shield,
  Home,
} from 'lucide-react';

/**
 * Pagina gestione utenti (admin)
 */
function UsersPage() {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Filtri
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog conferma disattivazione
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Dialog conferma eliminazione definitiva
  const [permanentDeleteDialog, setPermanentDeleteDialog] = useState({ open: false, user: null });
  const [isPermanentDeleting, setIsPermanentDeleting] = useState(false);

  // Dialog dettagli utente
  const [detailsDialog, setDetailsDialog] = useState({ open: false, user: null });

  useEffect(() => {
    loadUsers();
  }, [pagination.page, roleFilter, statusFilter]);

  // Debounce ricerca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        loadUsers();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search) params.search = search;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';

      const response = await userService.getAll(params);
      setUsers(response.data?.users || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0,
      }));
    } catch (error) {
      toast.error('Errore', 'Impossibile caricare gli utenti');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;

    setIsDeleting(true);
    try {
      await userService.delete(deleteDialog.user._id);
      toast.success('Utente disattivato', `${deleteDialog.user.email} è stato disattivato`);
      setDeleteDialog({ open: false, user: null });
      loadUsers();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile disattivare l\'utente');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePermanentDelete = async () => {
    if (!permanentDeleteDialog.user) return;

    setIsPermanentDeleting(true);
    try {
      const response = await userService.permanentDelete(permanentDeleteDialog.user._id);
      toast.success('Utente eliminato', response.message);
      setPermanentDeleteDialog({ open: false, user: null });
      loadUsers();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile eliminare l\'utente');
    } finally {
      setIsPermanentDeleting(false);
    }
  };

  const getRoleBadge = (role) => {
    const variants = {
      admin: 'destructive',
      operator: 'info',
      client: 'secondary',
    };
    const labels = {
      admin: 'Admin',
      operator: 'Operatore',
      client: 'Cliente',
    };
    return <Badge variant={variants[role]}>{labels[role]}</Badge>;
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge variant="success">Attivo</Badge>
    ) : (
      <Badge variant="warning">Disattivato</Badge>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Gestione Utenti</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {pagination.total} utenti totali
          </p>
        </div>
        <Link to="/users/new" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto min-h-[44px] touch-manipulation">
            <Plus className="mr-2 h-4 w-4" />
            Nuovo Utente
          </Button>
        </Link>
      </div>

      {/* Filtri */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome, email, ospite..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 min-h-[44px] text-base"
          />
        </div>

        <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-4">
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full sm:w-[150px] min-h-[44px]">
              <SelectValue placeholder="Ruolo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i ruoli</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="operator">Operatore</SelectItem>
              <SelectItem value="client">Cliente</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[150px] min-h-[44px]">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="inactive">Disattivati</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-card rounded-lg border border-border">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-primary" size={32} />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nessun utente trovato</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utente</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead className="hidden md:table-cell">Ospite</TableHead>
                <TableHead className="hidden lg:table-cell">Operatore</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user._id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-medium">
                          {user.firstName?.[0]}
                          {user.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {user.guestName ? (
                      <div>
                        <p>{user.guestName}</p>
                        {user.guestRoom && (
                          <p className="text-sm text-muted-foreground">
                            Stanza {user.guestRoom}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {user.role === 'admin' || user.role === 'operator' ? (
                      <span className="text-muted-foreground">-</span>
                    ) : user.assignedOperator ? (
                      <p>
                        {user.assignedOperator.firstName} {user.assignedOperator.lastName}
                      </p>
                    ) : (
                      <span className="text-muted-foreground">Non assegnato</span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(user.isActive)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Dettagli"
                        onClick={() => setDetailsDialog({ open: true, user })}
                        className="min-h-[40px] min-w-[40px] touch-manipulation"
                      >
                        <Eye className="h-4 w-4 text-primary" />
                      </Button>
                      <Link to={`/users/${user._id}/edit`}>
                        <Button variant="ghost" size="icon" title="Modifica" className="min-h-[40px] min-w-[40px] touch-manipulation">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <span className="hidden sm:inline-flex gap-1">
                        {user.isActive ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Disattiva"
                            onClick={() => setDeleteDialog({ open: true, user })}
                            className="min-h-[40px] min-w-[40px] touch-manipulation"
                          >
                            <UserX className="h-4 w-4 text-destructive" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" title="Già disattivato" disabled className="min-h-[40px] min-w-[40px]">
                            <UserCheck className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        {user.role !== 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Elimina definitivamente"
                            onClick={() => setPermanentDeleteDialog({ open: true, user })}
                            className="min-h-[40px] min-w-[40px] touch-manipulation"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Paginazione */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Pagina {pagination.page} di {pagination.pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Dialog conferma disattivazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disattiva utente</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler disattivare l'utente{' '}
              <strong>
                {deleteDialog.user?.firstName} {deleteDialog.user?.lastName}
              </strong>
              ? L'utente non potrà più accedere al sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, user: null })}
            >
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={isDeleting}>
              Disattiva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione definitiva */}
      <Dialog open={permanentDeleteDialog.open} onOpenChange={(open) => setPermanentDeleteDialog({ open, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina definitivamente</DialogTitle>
            <DialogDescription>
              <span className="text-destructive font-semibold">Attenzione: questa azione è irreversibile!</span>
              <br /><br />
              Stai per eliminare definitivamente l'utente{' '}
              <strong>
                {permanentDeleteDialog.user?.firstName} {permanentDeleteDialog.user?.lastName}
              </strong>
              {permanentDeleteDialog.user?.role === 'client' && ' e tutti i suoi documenti associati'}.
              <br /><br />
              I dati non potranno essere recuperati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPermanentDeleteDialog({ open: false, user: null })}
            >
              Annulla
            </Button>
            <Button variant="destructive" onClick={handlePermanentDelete} isLoading={isPermanentDeleting}>
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog dettagli utente */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => setDetailsDialog({ open, user: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Dettagli Utente</DialogTitle>
          </DialogHeader>
          {detailsDialog.user && (
            <div className="space-y-6">
              {/* Header con avatar e info principali */}
              <div className="flex items-center gap-4 pb-4 border-b border-border">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary text-xl font-medium">
                    {detailsDialog.user.firstName?.[0]}
                    {detailsDialog.user.lastName?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {detailsDialog.user.firstName} {detailsDialog.user.lastName}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getRoleBadge(detailsDialog.user.role)}
                    {getStatusBadge(detailsDialog.user.isActive)}
                  </div>
                </div>
              </div>

              {/* Informazioni di contatto */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase">Contatti</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{detailsDialog.user.email}</span>
                  </div>
                  {detailsDialog.user.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{detailsDialog.user.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Info ospite (solo per clienti) */}
              {detailsDialog.user.role === 'client' && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase">Informazioni Ospite</h4>
                  <div className="space-y-2">
                    {detailsDialog.user.guestName && (
                      <div className="flex items-center gap-3">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span><strong>Nome ospite:</strong> {detailsDialog.user.guestName}</span>
                      </div>
                    )}
                    {detailsDialog.user.guestRoom && (
                      <div className="flex items-center gap-3">
                        <Home className="h-4 w-4 text-muted-foreground" />
                        <span><strong>Stanza:</strong> {detailsDialog.user.guestRoom}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <UserCog className="h-4 w-4 text-muted-foreground" />
                      <span>
                        <strong>Operatore:</strong>{' '}
                        {detailsDialog.user.assignedOperator
                          ? `${detailsDialog.user.assignedOperator.firstName} ${detailsDialog.user.assignedOperator.lastName}`
                          : 'Non assegnato'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Sicurezza e sistema */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase">Sistema</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span>
                      <strong>2FA:</strong>{' '}
                      {detailsDialog.user.twoFactorEnabled ? (
                        <Badge variant="success">Attivo</Badge>
                      ) : (
                        <Badge variant="secondary">Non attivo</Badge>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      <strong>Registrato il:</strong>{' '}
                      {new Date(detailsDialog.user.createdAt).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  {detailsDialog.user.lastLogin && (
                    <div className="flex items-center gap-3">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>
                        <strong>Ultimo accesso:</strong>{' '}
                        {new Date(detailsDialog.user.lastLogin).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDetailsDialog({ open: false, user: null })}
            >
              Chiudi
            </Button>
            <Link to={`/users/${detailsDialog.user?._id}/edit`}>
              <Button>
                <Edit className="mr-2 h-4 w-4" />
                Modifica
              </Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UsersPage;
